/**
 * Whether the level's 3D scene renders this frame (M30 #11, the completion screen's frame rate). Under the end
 * screen the run is over and the battlefield is covered by the end screen's dim (and, with a chest, by a second WebGL
 * context — the 3D chest with its own bloom — rendering at 60 Hz beside it). Rendering the whole level underneath
 * every frame doubled the GPU work of that screen for a picture the player can barely see. Once the end screen has
 * faded in, the level is drawn only when its canvas changes size, the page comes back to the front (iOS may drop
 * the canvas's contents in the background), or every RefreshMs as insurance; the canvas keeps its last frame
 * in between.
 *
 * Pure: the level loop passes whether the end screen is up, the frame time and a key for the canvas's state.
 */
export class EndScreenRender {
  /** The end screen's entrance (styles/end.css `end-in`, 0.4 s) plus a margin: the battle stays live under it. */
  static readonly SettleMs = 700;
  /** The slowest the frozen level is re-drawn, whatever happens. */
  static readonly RefreshMs = 2000;
  private shownAt = -1;
  private lastAt = -1;
  private key = "";
  /** Frames the level skipped since the end screen came up (for the tests and the perf overlay). */
  skipped = 0;

  want(endShown: boolean, now: number, key: string): boolean {
    if (!endShown) { this.shownAt = -1; return true; }
    if (this.shownAt < 0) this.shownAt = now;
    if (now - this.shownAt < EndScreenRender.SettleMs || key !== this.key || now - this.lastAt >= EndScreenRender.RefreshMs || now < this.lastAt) {
      this.key = key;
      this.lastAt = now;
      return true;
    }
    this.skipped++;
    return false;
  }

  /**
   * M33: the end screen has settled over the level: after this frame's draw the rig may rest (SceneRig.rest — the
   * canvas keeps the frame as a still and gives back its post chain, shadow map and drawing buffer), so the end
   * screen's 3D chest never holds its own WebGL memory beside the live level's. Once rested the level draws no more.
   */
  restDue(now: number): boolean { return this.shownAt >= 0 && now - this.shownAt >= EndScreenRender.SettleMs; }
}
